import { isFailure, isSuccess } from '@devexperts/remote-data-ts'
import { BookingPeriod } from '@spiaggeit/spit-datepicker'
import { cn } from '@spiaggeit/spit-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDebounceCallback } from 'usehooks-ts'

import { LoaderTrigger } from '@/components/LoaderTrigger'
import { useFetchPricePreview } from '@/hooks/useFetchPricePreview.ts'
import { useNextPathAfterChoosingSpot } from '@/hooks/useNextPathAfterChoosingSpot.ts'
import { BoughtServicesRecord } from '@/models/quote'
import { ServiceInfoPurchaseType } from '@/models/service'
import { isKioskSelector } from '@/store/appSlice'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice'
import { selectCartPayload } from '@/store/cartSlice/selectors/selectCartPayload.ts'
import { ticketsInCartTotalQuantity } from '@/store/cartSlice/selectors/servicesWithCartQuantitySelector'
import { ticketPageErrorsSelector } from '@/store/cartSlice/selectors/ticketPageErrorsSelector'
import { licenseSlice } from '@/store/licenseSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { ONE_MINUTE } from '@/utils/constants'
import { fromISOtoTimestamp } from '@/utils/dateUtils'
import { getServiceUpdatedQuantity } from '@/utils/getServiceUpdatedQuantity'
import { getHalfDayValueFromBookingPeriod } from '@/utils/halfDay'

import { getDaysFromPriceMode, getTimeScheduleKey } from '@/utils/timeSchedules'
import { KioskTimeoutDialog } from '../../components/Dialog/KioskTimeoutDialog'
import { FeesDialog } from '../../components/FeesDialog'
import { useAppDispatch, useAppSelector } from '../../hooks/store'
import { cartSlice } from '../../store/cartSlice'
import {
  bookingPeriodSelector,
  dayDifferenceSelector,
  insertPeriodSlice,
  periodAsUnixTimeSelector,
} from '../../store/insertPeriodSlice'
import {
  filteredGroupsSelector,
  filteredServicesSelector,
  servicesSlice,
} from '../../store/servicesSlice'
import { toastSlice } from '../../store/toastSlice'

import { Group } from './Group'
import { MobileDrawer } from './MobileDrawer'
import { Sidebar } from './Sidebar'

export const TicketsRoute = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [isFeesDialogOpen, setIsFeesDialogOpen] = useState(false)
  const services = useAppSelector(filteredServicesSelector)
  const quoteRemoteData = useAppSelector(cartSlice.selectors.quote)
  const visibleGroups = useAppSelector(filteredGroupsSelector)
  const totalQuantity = useAppSelector(ticketsInCartTotalQuantity)
  const cartPayload = useAppSelector(selectCartPayload)
  const inputRef = useRef<HTMLInputElement>(null)
  const period = useAppSelector(insertPeriodSlice.selectors.period)
  const periodUnix = useAppSelector(periodAsUnixTimeSelector)
  const bookingPeriod = useAppSelector(bookingPeriodSelector)
  const license = useAppSelector(licenseSlice.selectors.license)
  const [isLoading, setIsLoading] = useState(false)
  const nextPath = useNextPathAfterChoosingSpot()
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const errors = useAppSelector(ticketPageErrorsSelector)
  const [searchParams] = useSearchParams()

  const quoteCallback = useCallback(() => {
    dispatch(cartSlice.actions.quote({ isQuoteForTickets: true }))
  }, [dispatch])

  const quote = useDebounceCallback(quoteCallback, 500)

  useEffect(() => {
    dispatch(cartSlice.actions.setBookingType(BookingAvailability.TICKETS))

    return () => {
      dispatch(ticketSlice.actions.setSubmitted(false))
    }
  }, [])

  useEffect(() => {
    dispatch(
      servicesSlice.actions.load({
        purchaseType: ServiceInfoPurchaseType.ALONE,
      })
    )

    dispatch(servicesSlice.actions.fetchFeaturedServices())
  }, [dispatch, period])

  useEffect(() => {
    if (!isSuccess(services) || !periodUnix) {
      return
    }
    const boughtServices: BoughtServicesRecord =
      services.value.ticketServices.reduce<BoughtServicesRecord>(
        (acc, service) => {
          if (service.scheduleType !== 'daily') {
            return acc
          }

          const initialQuantity = getServiceUpdatedQuantity({
            dayDifference,
            selectedSeats: 0,
            service,
          })

          if (initialQuantity === 0) {
            return acc
          }

          // check if sellable every day
          const isSellable = Object.values(service.dailyTimeSchedules).every(
            (schedules) => {
              if (schedules.length === 0) {
                return false
              }
              return schedules.some(
                (schedule) => schedule.availableQuantity > 0
              )
            }
          )
          if (!isSellable) {
            return acc
          }

          const days = getDaysFromPriceMode(service, dayDifference)

          const bought =
            service.availableQuantity >= initialQuantity
              ? initialQuantity
              : service.availableQuantity

          acc[service.serviceId] = {
            [getTimeScheduleKey(service.timeSchedules[0])]: {
              bought,
              days,
              serviceId: service.serviceId,
              serviceName: service.name,
              timeSchedule: service.timeSchedules[0],
            },
          }

          return acc
        },
        {}
      )

    dispatch(
      cartSlice.actions.setCartReservations([
        {
          beds: 0,
          boughtServices,
          chairs: 0,
          deckChairs: 0,
          endDate: Number(fromISOtoTimestamp(period?.end)),
          halfDay: getHalfDayValueFromBookingPeriod(bookingPeriod),
          maxiBeds: 0,
          notes: '',
          seasonal: bookingPeriod === BookingPeriod.ALL_SEASON,
          sectorId: 0,
          sectorName: '',
          spotName: null,
          spotType: null,
          startDate: Number(fromISOtoTimestamp(period?.start)),
        },
      ])
    )
    quote()
  }, [services])

  useEffect(() => {
    quote()
  }, [cartReservations])

  useEffect(() => {
    if (inputRef.current && cartPayload) {
      inputRef.current.value = JSON.stringify(cartPayload)
    }
  }, [cartPayload])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const target = e.target
    e.preventDefault()

    if (errors.length > 0) {
      dispatch(ticketSlice.actions.setSubmitted(true))
      return
    }

    if (!isSuccess(services)) return
    setIsLoading(true)
    if (!(target instanceof HTMLFormElement)) {
      dispatch(toastSlice.actions.show({ message: t('tickets.error.generic') }))
      setIsLoading(false)
    } else if (
      services.value.hasLimit &&
      services.value.availableQuantity < totalQuantity
    ) {
      dispatch(toastSlice.actions.show({ message: t('tickets.error.soldOut') }))
      setIsLoading(false)
    } else {
      dispatch(cartSlice.actions.quote({ isQuoteForTickets: true }))
        .then(() => {
          if (totalQuantity === 0) {
            dispatch(
              toastSlice.actions.show({ message: t('tickets.error.min') })
            )
          } else if (isFailure(quoteRemoteData)) {
            dispatch(
              toastSlice.actions.show({ message: t('tickets.error.generic') })
            )
          } else {
            navigate({
              pathname: nextPath,
              search: searchParams.toString(),
            })
          }
        })
        .finally(() => setIsLoading(false))
    }
  }

  const isLoadingPricePreview = useFetchPricePreview()

  const isKiosk = useAppSelector(isKioskSelector)

  if (!isSuccess(services) || !license || isLoadingPricePreview) {
    return <LoaderTrigger />
  }

  const hasReachedLimit = services.value.hasLimit
    ? totalQuantity === services.value.availableQuantity
    : false

  return (
    <>
      <main
        className={cn('grow pb-28 pt-4 md:pb-16 md:pt-8', {
          'pb-32 pt-4': isKiosk,
        })}
      >
        <form className="container" onSubmit={onSubmit}>
          <input
            id="ybBookingServicesFormCart"
            name="cart"
            ref={inputRef}
            type="hidden"
          />

          <div className="md:grid md:grid-cols-12 md:gap-8">
            <div className="md:col-span-7 lg:col-span-8">
              <div
                className={cn(
                  'mb-8 rounded-sm border-l-4 px-3 py-2 text-sm md:text-base',
                  {
                    'border-l-red-300 bg-red-50': hasReachedLimit,
                    'border-l-yellow-300 bg-yellow-50': !hasReachedLimit,
                    'mb-4': isKiosk,
                  }
                )}
              >
                {hasReachedLimit ? (
                  <p>
                    <Trans i18nKey="tickets.banner.soldOut" />
                  </p>
                ) : null}

                <p>
                  <Trans i18nKey="tickets.banner.generic" />
                </p>
              </div>

              {visibleGroups.map((group) => (
                <Group
                  availableQuantity={group.availableQuantity}
                  hasLimit={group.hasLimit}
                  key={group.serviceGroupId}
                  maximumQuantity={group.maximumQuantity}
                  name={group.name}
                  reservedQuantity={group.reservedQuantity}
                  serviceGroupId={group.serviceGroupId}
                  tickets={group.services}
                />
              ))}
            </div>

            <div className="hidden md:col-span-5 md:block lg:col-span-4">
              <Sidebar
                isLoading={isLoading}
                openFeesDialog={() => setIsFeesDialogOpen(true)}
              />
            </div>
          </div>

          <MobileDrawer openFeesDialog={() => setIsFeesDialogOpen(true)} />
        </form>

        <FeesDialog isOpen={isFeesDialogOpen} open={setIsFeesDialogOpen} />
      </main>

      <KioskTimeoutDialog timeout={ONE_MINUTE * 3} />
    </>
  )
}
